/**
 * Temporal attention-state engine (pipeline stage 7).
 *
 * Converts a stream of direction samples (and, from Phase 2, activity signals) into the §8
 * state machine. Its job is as much about *refusing to conclude* as concluding.
 *
 * Rules that exist specifically to avoid the failure modes §10 forbids:
 *
 * - A head turned away does not become `possibly_distracted` until it has been away for
 *   `attentionAwayMinSeconds`. Glancing at a colleague, a phone or a second monitor is
 *   normal work behaviour and produces no state change at all.
 * - No face is `away` only after `faceLostAwaySeconds`; before that it is `unknown`, because
 *   a hand across the face and leaving the desk look identical for the first second.
 * - Absence of keyboard/mouse input is `idle` — its own state, never distraction. Reading,
 *   thinking and listening are all legitimately idle.
 * - Low pose confidence, or more than one face in frame, is `unknown`. Uncertain data never
 *   becomes an adverse finding.
 * - When a state rests on a single modality (Phase 1 has no activity signals), the reported
 *   confidence is scaled down and the missing modality is named in the evidence.
 *
 * Confidence semantics, consistent everywhere: **confidence is confidence in the state that
 * was reported.** For `unknown` that means confidence that the data does not support a
 * conclusion — high when the camera clearly shows nothing usable. Consumers must still treat
 * `unknown` as an absence of information; `isConclusive` exists so scoring cannot mistake one
 * for the other.
 */

import type { EventPolicy } from '../config.js';
import {
  clamp,
  createInference,
  isConclusive,
  type ActivitySignals,
  type AttentionInference,
  type AttentionState,
  type DirectionSample,
} from '../models.js';

/**
 * Confidence multiplier when a state rests on head orientation alone.
 *
 * Orientation is a proxy for facing the workstation, not evidence of work. With no keyboard,
 * mouse or application context to corroborate it, the honest response is to report the state
 * with visibly reduced confidence rather than as firmly as a corroborated one.
 */
export const SINGLE_MODALITY_CONFIDENCE_FACTOR = 0.8;

export const AWAY_CONFIDENCE_FLOOR = 0.5;
export const AWAY_CONFIDENCE_CEILING = 0.95;
export const DETERMINISTIC_SIGNAL_CONFIDENCE = 0.95;
export const UNKNOWN_CONFIDENCE_FLOOR = 0.5;
export const UNKNOWN_CONFIDENCE_CEILING = 0.99;

export interface StateEvaluation {
  readonly inference: AttentionInference;
  readonly changed: boolean;
  readonly previousState: AttentionState;
  readonly previousStateDurationSeconds: number;
  /** Monotonic time at which the current state began. */
  readonly committedAt: number;
}

export interface StateEngineOptions {
  minPoseConfidence: number;
  collectedSignals?: readonly string[];
  singleModalityFactor?: number;
  initialState?: AttentionState;
}

export class AttentionStateEngine {
  private readonly policy: EventPolicy;
  private readonly minPoseConfidence: number;
  private readonly collected: ReadonlySet<string>;
  private readonly singleModalityFactor: number;

  private currentState: AttentionState;
  private stateSince: number | null = null;
  private candidate: AttentionState | null = null;
  private candidateSince = 0;
  private faceMissingSince: number | null = null;
  private awayRunSinceTs: number | null = null;

  constructor(policy: EventPolicy, options: StateEngineOptions) {
    this.policy = policy;
    this.minPoseConfidence = options.minPoseConfidence;
    this.collected = new Set(options.collectedSignals ?? []);
    this.singleModalityFactor = options.singleModalityFactor ?? SINGLE_MODALITY_CONFIDENCE_FACTOR;
    this.currentState = options.initialState ?? 'unknown';
  }

  get state(): AttentionState {
    return this.currentState;
  }

  /** Start of the current continuous non-forward orientation run, if any. */
  get awayRunSince(): number | null {
    return this.awayRunSinceTs;
  }

  stateDuration(now: number): number {
    if (this.stateSince === null) return 0;
    return Math.max(0, now - this.stateSince);
  }

  reset(now: number): void {
    this.currentState = 'unknown';
    this.stateSince = now;
    this.candidate = null;
    this.faceMissingSince = null;
    this.awayRunSinceTs = null;
  }

  observe(sample: DirectionSample, activity: ActivitySignals | null = null): StateEvaluation {
    const now = sample.monotonicTs;
    if (this.stateSince === null) this.stateSince = now;

    this.trackRuns(sample, now);
    const evaluated = this.evaluate(sample, activity, now);
    let evidence = evaluated.evidence;

    const previousState = this.currentState;
    const previousDuration = this.stateDuration(now);
    let changed = false;

    if (evaluated.target === this.currentState) {
      this.candidate = null;
    } else if (this.needsDwell(evaluated.target) && this.policy.minStateDwellSeconds > 0) {
      if (this.candidate !== evaluated.target) {
        this.candidate = evaluated.target;
        this.candidateSince = now;
        evidence = [...evidence, this.holdNote(evaluated.target)];
      } else if (now - this.candidateSince >= this.policy.minStateDwellSeconds) {
        this.commit(evaluated.target, now);
        changed = true;
        this.candidate = null;
      } else {
        evidence = [...evidence, this.holdNote(evaluated.target)];
      }
    } else {
      this.commit(evaluated.target, now);
      changed = true;
      this.candidate = null;
    }

    const inference = createInference({
      timestampUtc: sample.timestampUtc,
      monotonicTs: now,
      state: this.currentState,
      confidence: clamp(evaluated.confidence, 0, 1),
      direction: sample.direction,
      evidence,
      smoothedPose: sample.smoothedPose,
      stateDurationSeconds: this.stateDuration(now),
    });

    return {
      inference,
      changed,
      previousState,
      previousStateDurationSeconds: previousDuration,
      committedAt: this.stateSince ?? now,
    };
  }

  private holdNote(target: AttentionState): string {
    return (
      `holding ${this.currentState} until ${target} persists for ` +
      `${this.policy.minStateDwellSeconds.toFixed(0)}s`
    );
  }

  private commit(state: AttentionState, now: number): void {
    this.currentState = state;
    this.stateSince = now;
  }

  /**
   * Dwell debounce applies only to states with no duration rule of their own.
   *
   * `possibly_distracted`, `away` and `idle` are already time-gated by their own thresholds;
   * adding a dwell timer on top would silently make the effective threshold longer than the
   * configured one, which an administrator reading the policy would never expect.
   *
   * It also does not apply when leaving an inconclusive state. Dwell exists to stop flapping
   * *between findings*; coming out of "we do not know yet" there is nothing to flap from, and
   * holding `unknown` for another two seconds only discards monitored time at the start of
   * every session and after every camera recovery.
   */
  private needsDwell(target: AttentionState): boolean {
    if (!isConclusive(this.currentState)) return false;
    return target === 'focused' || target === 'meeting';
  }

  private trackRuns(sample: DirectionSample, now: number): void {
    if (sample.direction === 'not_detected') {
      // Only an absent face starts an absence clock. A face that is visible but whose
      // orientation could not be solved is a measurement failure, not someone leaving.
      const faceAbsent = sample.quality === null || isFaceAbsentQuality(sample.quality);
      if (faceAbsent) {
        if (this.faceMissingSince === null) this.faceMissingSince = now;
      } else {
        this.faceMissingSince = null;
      }
      return;
    }

    this.faceMissingSince = null;
    if (sample.direction === 'forward') {
      this.awayRunSinceTs = null;
    } else if (this.awayRunSinceTs === null) {
      this.awayRunSinceTs = now;
    }
  }

  private evaluate(
    sample: DirectionSample,
    activity: ActivitySignals | null,
    now: number,
  ): { target: AttentionState; evidence: string[]; confidence: number } {
    const modalityNote = this.modalityNote(activity);
    let dataConfidence = sample.confidence;
    if (activity === null) dataConfidence *= this.singleModalityFactor;

    // 1. Meeting overrides ordinary orientation rules (§15).
    if (activity !== null && activity.meetingActive && this.collected.has('meeting_state')) {
      return {
        target: 'meeting',
        evidence: [
          'an approved meeting application is active',
          'ordinary attention-away rules are relaxed during meetings',
          ...sample.reasons,
        ],
        confidence: DETERMINISTIC_SIGNAL_CONFIDENCE,
      };
    }

    // 2. No measurable orientation.
    if (sample.direction === 'not_detected') {
      const faceAbsent = sample.quality === null || isFaceAbsentQuality(sample.quality);
      const baseEvidence =
        sample.reasons.length > 0 ? [...sample.reasons] : ['no face visible to the camera'];

      if (!faceAbsent) {
        return {
          target: 'unknown',
          evidence: [
            ...baseEvidence,
            'a face is visible but its orientation could not be measured; this is a ' +
              'measurement failure, not an absence from the workstation',
          ],
          confidence: unknownConfidence(dataConfidence),
        };
      }

      const missingFor = now - (this.faceMissingSince ?? now);
      if (missingFor >= this.policy.faceLostAwaySeconds) {
        const evidence = [
          ...baseEvidence,
          `no face detected for ${missingFor.toFixed(0)}s, beyond the ` +
            `${this.policy.faceLostAwaySeconds.toFixed(0)}s absence threshold`,
        ];
        if (activity !== null) {
          evidence.push(
            `computer input in this period: ${
              activity.keyboardActivity || activity.mouseActivity ? 'present' : 'none'
            }`,
          );
        }
        return {
          target: 'away',
          evidence,
          confidence: ramp(
            missingFor,
            this.policy.faceLostAwaySeconds,
            this.policy.faceLostAwaySeconds * 3,
            AWAY_CONFIDENCE_FLOOR,
            AWAY_CONFIDENCE_CEILING,
          ),
        };
      }

      return {
        target: 'unknown',
        evidence: [
          ...baseEvidence,
          `no face for ${missingFor.toFixed(0)}s, below the ` +
            `${this.policy.faceLostAwaySeconds.toFixed(0)}s absence threshold; too early to ` +
            'distinguish a brief occlusion from leaving the workstation',
        ],
        confidence: unknownConfidence(dataConfidence),
      };
    }

    // 3. Data we cannot trust.
    if (sample.quality !== null && sample.quality.flags.includes('multiple_faces')) {
      return {
        target: 'unknown',
        evidence: [
          'more than one face is visible, so orientation cannot be attributed to a single person',
          ...sample.reasons,
        ],
        confidence: unknownConfidence(dataConfidence),
      };
    }

    if (sample.confidence < this.minPoseConfidence) {
      return {
        target: 'unknown',
        evidence: [
          `orientation confidence ${sample.confidence.toFixed(2)} is below the ` +
            `${this.minPoseConfidence.toFixed(2)} threshold required to draw a conclusion`,
          ...sample.reasons,
        ],
        confidence: unknownConfidence(dataConfidence),
      };
    }

    // 4. Idle takes precedence over orientation: someone reading at their desk is present
    //    and facing forward, and calling that "focused" overstates what we know.
    if (
      activity !== null &&
      this.collected.has('idle_duration') &&
      activity.idleDurationSeconds >= this.policy.idleTimeoutSeconds
    ) {
      return {
        target: 'idle',
        evidence: [
          `no keyboard or mouse input for ${activity.idleDurationSeconds.toFixed(0)}s ` +
            `(threshold ${this.policy.idleTimeoutSeconds.toFixed(0)}s)`,
          'low computer interaction is reported as idle time, not as distraction',
          ...sample.reasons,
        ],
        confidence: DETERMINISTIC_SIGNAL_CONFIDENCE,
      };
    }

    // 5. Facing the workstation.
    if (sample.direction === 'forward') {
      const evidence = ['oriented toward the workstation', ...sample.reasons];
      evidence.push(...activityEvidence(activity));
      if (modalityNote !== null) evidence.push(modalityNote);
      return { target: 'focused', evidence, confidence: dataConfidence };
    }

    // 6. Oriented away — duration decides whether it is worth reporting.
    const awayFor = now - (this.awayRunSinceTs ?? now);
    if (awayFor >= this.policy.attentionAwayMinSeconds) {
      const evidence = [
        `oriented away from the workstation for ${awayFor.toFixed(0)}s ` +
          `(threshold ${this.policy.attentionAwayMinSeconds.toFixed(0)}s)`,
        ...sample.reasons,
        ...activityEvidence(activity),
        'orientation away from the workstation has legitimate explanations (a second ' +
          'monitor, printed documents, a colleague) and is one signal only',
      ];
      if (modalityNote !== null) evidence.push(modalityNote);
      return { target: 'possibly_distracted', evidence, confidence: dataConfidence };
    }

    const evidence = [
      `brief orientation change (${awayFor.toFixed(0)}s), below the ` +
        `${this.policy.attentionAwayMinSeconds.toFixed(0)}s threshold for an attention-away event`,
      ...sample.reasons,
    ];
    if (modalityNote !== null) evidence.push(modalityNote);
    // Remaining at the workstation with a brief glance elsewhere is ordinary work.
    return { target: 'focused', evidence, confidence: dataConfidence };
  }

  private modalityNote(activity: ActivitySignals | null): string | null {
    if (activity !== null) return null;
    const missing = (
      [
        ['keyboard/mouse activity', 'keyboard_activity'],
        ['application context', 'application_category'],
        ['meeting state', 'meeting_state'],
      ] as const
    )
      .filter(([, family]) => !this.collected.has(family))
      .map(([name]) => name);

    if (missing.length === 0) {
      return (
        'activity signals are enabled but were not supplied for this sample; confidence ' +
        'reduced accordingly'
      );
    }
    return (
      `based on head orientation alone; ${missing.join(', ')} ` +
      'are not collected in this configuration, so confidence is reduced'
    );
  }
}

function activityEvidence(activity: ActivitySignals | null): string[] {
  if (activity === null) return [];
  const parts: string[] = [];
  if (activity.keyboardActivity || activity.mouseActivity) {
    parts.push(
      `computer interaction present (${activity.keyboardEventsPerMinute.toFixed(0)} keyboard ` +
        `and ${activity.mouseEventsPerMinute.toFixed(0)} mouse events per minute)`,
    );
  } else {
    parts.push(`no computer interaction for ${activity.idleDurationSeconds.toFixed(0)}s`);
  }
  if (activity.applicationCategory !== null) {
    const relevance =
      activity.workRelevant === true
        ? 'work-relevant'
        : activity.workRelevant === false
          ? 'not classified as work-relevant'
          : 'work relevance not configured';
    parts.push(`active application category '${activity.applicationCategory}' (${relevance})`);
  }
  return parts;
}

function isFaceAbsentQuality(quality: { flags: readonly string[] }): boolean {
  return quality.flags.includes('no_face') || quality.flags.includes('camera_unavailable');
}

/** Confidence that no conclusion can be drawn — high when the data is clearly bad. */
function unknownConfidence(dataConfidence: number): number {
  return clamp(1 - dataConfidence, UNKNOWN_CONFIDENCE_FLOOR, UNKNOWN_CONFIDENCE_CEILING);
}

function ramp(
  value: number,
  low: number,
  high: number,
  lowValue: number,
  highValue: number,
): number {
  if (high <= low) return highValue;
  const fraction = clamp((value - low) / (high - low), 0, 1);
  return lowValue + (highValue - lowValue) * fraction;
}
